import { useMemo, useEffect, useState } from 'react'
import { generatePath, Navigate, useParams } from 'react-router-dom'
import { Space, Alert } from 'antd'
import { useForm, FormProvider } from 'react-hook-form'
import { Button } from 'antd'

import { COUNTRY } from '@qflow/constants'
import { Queries } from 'admin/lib/Api'
import { ScreenSpin } from 'admin/lib/ScreenSpin'
import { Breadcrumb, BreadcrumbArrayItem } from 'admin/lib/Breadcrumbs'
import { Layout } from 'admin/lib/Layouts'
import { Flag } from 'admin/lib/Icons/Flag'
import * as ROUTES from 'admin/routes'

import { Delivery } from './Delivery'
import { Waste } from './Waste'

import { AddInfractionButton } from 'admin/lib/ManualInfractions'
import { QuickLink } from 'admin/lib/QuickLink/QuickLink'
import { RecordDotMenuButton } from 'admin/lib/RecordDotMenu'
import { RecordHistoryButton } from 'admin/lib/RecordHistoryButton/RecordHistoryButton'
import { ShortcutsModalButton } from 'admin/lib/ShortcutsModalButton'
import { WikiLinkButton } from 'admin/lib/WikiLinkButton'

import {
  InfractionsProvider,
  recordTransformer,
  useRecordLoader,
  getFields,
  ModuleLabels
} from 'admin/lib/Records'
import { ViewInternalNotesButton } from 'admin/lib/Comments/InternalNotesButton'

const ModuleViews = {
  delivery: Delivery,
  waste: Waste,
  default: () => `No View for Module`
}

export function RecordEdit({ contextLoader }) {
  const [saving, setSaving] = useState(false)
  const [showHistory, setShowHistory] = useState(false)
  const { accountId, projectId, moduleName, recordId } = useParams()

  const fields = useMemo(() => getFields(moduleName), [moduleName])

  const isImperial = contextLoader?.project?.country === COUNTRY.USA

  const recordLoader = useRecordLoader(
    accountId,
    projectId,
    moduleName,
    recordId
  )
  const transformedRecord = useMemo(
    () => recordTransformer(recordLoader.record, moduleName, isImperial),
    [isImperial, moduleName, recordLoader.record]
  )

  useEffect(() => {
    if (recordLoader.loaded) {
      setSaving(false)
    }
  }, [recordLoader])

  const recordUpdater = Queries.useRecordUpdater()

  const loaded =
    contextLoader.loaded &&
    recordLoader.loaded &&
    !recordUpdater.isLoading &&
    !saving

  const ModuleView =
    ModuleViews[moduleName.toLowerCase()] ?? ModuleViews.default

  const form = useForm({
    reValidateMode: 'all',
    mode: 'all'
  })

  const movementId = form.watch(fields.movementId.path)
  useEffect(() => {
    document.title =
      'Edit: ' + (movementId ?? ModuleLabels[moduleName.toLowerCase()] ?? '')
  }, [moduleName, movementId])

  const handleRecordHistoryButtonClick = () => {
    setShowHistory(prev => !prev)
  }

  if (transformedRecord?.qcrAppliedToRaw) {
    return (
      <Navigate
        to={generatePath(ROUTES.RECORD_V2.EDIT, {
          accountId: accountId,
          projectId: projectId,
          moduleName: moduleName,
          recordId: recordId
        })}
      />
    )
  }

  return (
    <InfractionsProvider infractionsByPath={recordLoader.infractions}>
      <FormProvider {...form}>
        <Layout
          header={
            <>
              {contextLoader?.project?.country && (
                <Flag country={contextLoader.project.country} />
              )}

              <Breadcrumb
                items={[
                  {
                    title: (
                      <BreadcrumbArrayItem
                        loading={!contextLoader.loaded}
                        href={ROUTES.INTRAY}
                      >
                        {contextLoader.account?.name}
                      </BreadcrumbArrayItem>
                    )
                  },
                  {
                    title: (
                      <BreadcrumbArrayItem
                        loading={!contextLoader.loaded}
                        href={ROUTES.INTRAY}
                      >
                        {contextLoader.project?.name}
                      </BreadcrumbArrayItem>
                    )
                  },
                  {
                    title: (
                      <BreadcrumbArrayItem
                        loading={!contextLoader.loaded}
                        href={ROUTES.INTRAY}
                      >
                        {contextLoader.module}
                      </BreadcrumbArrayItem>
                    )
                  },
                  {
                    title: (
                      <BreadcrumbArrayItem loading={!contextLoader.loaded}>
                        {recordId}
                      </BreadcrumbArrayItem>
                    )
                  }
                ]}
              />

              <Space>
                {/* Temp button to take user to Raw & Enriched version of the record edit page */}
                <Button
                  disabled={!loaded}
                  href={generatePath(ROUTES.RECORD_V2.EDIT, {
                    accountId: accountId,
                    projectId: projectId,
                    moduleName: moduleName,
                    recordId: recordId
                  })}
                >
                  Switch to Raw/Enriched view
                </Button>
                <QuickLink
                  loaded={loaded}
                  record={recordLoader?.record}
                  accountId={accountId}
                  moduleName={moduleName}
                />
                <ViewInternalNotesButton
                  loaded={loaded}
                  record={recordLoader?.record}
                />
                <AddInfractionButton loaded={loaded} />
                <RecordHistoryButton
                  loaded={loaded}
                  hasHistory={recordLoader?.record?.history}
                  showHistory={showHistory}
                  onClick={handleRecordHistoryButtonClick}
                />
                {loaded && recordLoader?.record?.status && (
                  <RecordDotMenuButton
                    recordStatus={recordLoader?.record?.status}
                  />
                )}
                <ShortcutsModalButton />
                {loaded && (
                  <WikiLinkButton
                    moduleName={moduleName}
                    project={contextLoader.project}
                  />
                )}
              </Space>
            </>
          }
        >
          {loaded &&
            !recordLoader.error &&
            !contextLoader.error &&
            recordLoader.record && (
              <ModuleView
                record={transformedRecord}
                infractions={recordLoader.infractions}
                submit={() => {}}
                project={contextLoader.project}
                isImperial={isImperial}
                showHistory={showHistory}
                onHistoryCloseClick={handleRecordHistoryButtonClick}
              />
            )}

          {loaded && recordLoader.error && (
            <Alert
              message="Error"
              description={`Oh no! There was a problem loading this record, ${recordLoader.status}: ${recordLoader.error}`}
              type="error"
              showIcon
              style={{ marginTop: '20px' }}
            />
          )}

          {loaded && contextLoader.error && (
            <Alert
              message="Error"
              description={`There was a problem loading the context data for this record, ${contextLoader.error}`}
              type="error"
              showIcon
              style={{ marginTop: '20px' }}
            />
          )}

          {!loaded && <ScreenSpin />}
        </Layout>
      </FormProvider>
    </InfractionsProvider>
  )
}
